'use client';

import { useLocale, useTranslations } from 'next-intl';
import { memo, useEffect, useRef, useState } from 'react';
import {
  type BannerItemType,
  FeedList,
  useCommunityFeed,
  useCommunityInteraction,
  usePostReferenceFeed,
} from '@/app/components/shared/Community';
import { PostReferenceFilter } from '@/app/components/shared/PostReferenceFilter';
import { SearchEmpty } from '@/app/components/shared/SearchEmpty';
import { SearchInput, type SearchInputRef } from '@/app/components/shared/SearchInput';
import { SettingRadioGroup } from '@/app/components/shared/SettingRadioGroup';
import { toast } from '@/app/components/ui/use-toast';
import { HOME_PAGE, SORT_ICON_MAP, TRACE_EVENT_ID, TRACE_EVENT_NAME } from '@/app/constants';
import { useExtraTag, useFetch, useMediaMetrics, useUrlParams } from '@/app/hooks';
import { useStore } from '@/app/providers/store';
import type { SortByType } from '@/app/types';
import {
  CommunityEnum,
  InspirationActiveType,
  type SelectedAsset,
  type UpdateInteractionStatusParams,
} from '@/app/types';
import { cn, getAssetInfo, reportEvent } from '@/app/utils';
import { generateCommunityShareUrl } from '@/app/utils/client';
import { LANGUAGE_CONFIG } from '@/i18n/config';
import { usePathname, useRouter } from '@/i18n/routing';
import { materialsShareElementsFeed } from '../apis';
import type { UrlParams } from '../types';
import { useHomeGenerationFormActions } from './HomeGenerationFormActionsContext';
import { useHomeScroll } from './HomeLayoutClient';

interface FeedContainerProps {
  exploreTab: CommunityEnum;
  mixed?: boolean;
  className?: string;
  bannerList?: BannerItemType[];
  getBannerListLoading?: boolean;
  /** 覆盖内部 sticky filter 的 top 定位，用于上方有额外 sticky 层级时 */
  stickyFilterTopClassName?: string;
  /** 隐藏灵感的推荐/最新筛选 tab，始终使用推荐排序 */
  hideInspirationTypeFilter?: boolean;
}

const FeedContainerBase = ({
  exploreTab,
  mixed,
  className,
  bannerList,
  getBannerListLoading,
  stickyFilterTopClassName,
  hideInspirationTypeFilter,
}: FeedContainerProps) => {
  const t = useTranslations();
  const pathname = usePathname();
  const router = useRouter();
  const locale = useLocale();
  const homeGenerationFormActions = useHomeGenerationFormActions();

  const [selectedAsset, setSelectedAsset] = useState<SelectedAsset | undefined>();
  const [hasMore, setHasMore] = useState(false);
  const creationSerialId = useRef<number>(-1);
  const pageToken = useRef('');
  const isFirstFeedEffect = useRef(true);
  const [activeType, setActiveType] = useState<InspirationActiveType | undefined>(() =>
    mixed ? undefined : InspirationActiveType.Recommended,
  );
  const { collectTag, likeTag, getExtraTagName } = useExtraTag();
  const searchInputRef = useRef<SearchInputRef>(null);

  const inspirationTypeTabs = [
    {
      key: InspirationActiveType.Recommended,
      label: t('homeTab.recommended'),
    },
    {
      key: InspirationActiveType.Latest,
      label: t('homeTab.latest'),
    },
  ];

  const { scrollToTop } = useHomeScroll();

  // 解析主体分享
  useUrlParams<UrlParams>(
    'params',
    (params) => {
      if (params.id) {
        const isDefaultLocale = locale === LANGUAGE_CONFIG.default;
        const finalLink = isDefaultLocale ? pathname : `/${locale}${pathname}`;
        window.history.replaceState(null, '', finalLink);

        // 兼容以前分享出去的活动
        if (params.isActivity) {
          window.location.href = generateCommunityShareUrl({ id: params.id, isActivity: true });
          return;
        }

        // 主体分享打开详情
        setTimeout(() => {
          handleShareReferenceDetail(params.id);
        }, 0);
      }
    },
    (error) => {
      console.error('URL参数解析失败:', error);
    },
  );

  const { referenceModalUpdateReason, syncFeedDetailConfig } = useStore((state) => ({
    referenceModalUpdateReason: state.referenceModalUpdateReason,
    syncFeedDetailConfig: state.syncFeedDetailConfig,
  }));

  const updatePageInfo = ({ token }: { token?: string }) => {
    pageToken.current = token || '';
    setHasMore(!!token);
  };

  const updateCreationSerialId = (num: number) => {
    creationSerialId.current = num;
  };

  const { handleEnter, handleLeave, incrementCompleteTimes } = useMediaMetrics({
    callback: (detailStayTime: number, completeTimes: number) => {
      if (exploreTab === CommunityEnum.PostReferences) {
        return;
      }
      const { type, id = '', traceId = '' } = getAssetInfo(selectedAsset) || {};
      type &&
        reportEvent(TRACE_EVENT_NAME[type], {
          [TRACE_EVENT_ID[type]]: id,
          complete_times: completeTimes,
          detail_staytime: detailStayTime,
          interaction_position: pathname,
          interaction_type: 'view_detail',
          trace_id: traceId,
          content_type: 'video',
        });
    },
  });

  const { runAsync: fetchPostReference, loading: shareReferenceLoading } = useFetch(materialsShareElementsFeed, {
    manual: true,
  });

  const {
    referenceList,
    getShareReferenceData,
    handleDetail: handleShareReferenceDetail,
    updateShareReferenceInteractionStatus,
    categories,
    activeCategory,
    setActiveCategory,
    sortBy,
    init: initShareReferenceData,
    setSortBy,
    onExpose,
    setSearchValue,
    searchValue,
  } = usePostReferenceFeed({
    fetchData: fetchPostReference,
    fromPage: pathname.startsWith(HOME_PAGE) ? 'home' : 'explore',
    handleEnter,
    handleLeave,
    pageToken,
    updatePageInfo,
  });

  const {
    getData: getFeedData,
    updateInteractionStatus,
    init: initFeedData,
    loading: feedLoading,
    inspAssets,
  } = useCommunityFeed({
    activeType,
    exploreTab,
    handleEnter,
    mixed,
    pageToken,
    updateCreationSerialId,
    updatePageInfo,
  });

  useEffect(() => {
    pageToken.current = '';
    initFeedData();
    initShareReferenceData();
    setHasMore(false);
    exploreTab && getData();
    if (isFirstFeedEffect.current) {
      isFirstFeedEffect.current = false;
    } else {
      scrollToTop?.();
    }
  }, [activeCategory, sortBy, exploreTab, activeType, searchValue]);

  const requestShareReferenceData = () => {
    if (exploreTab === CommunityEnum.PostReferences) {
      pageToken.current = '';
      initShareReferenceData();
      setHasMore(false);
      getShareReferenceData();
    }
  };

  useEffect(() => {
    if (referenceModalUpdateReason?.type === 'forbidden') {
      requestShareReferenceData();
    }
  }, [referenceModalUpdateReason]);

  const getData = () => {
    if (exploreTab === CommunityEnum.PostReferences) {
      if (searchValue && [likeTag.id, collectTag.id].includes(activeCategory) && !pageToken.current) {
        toast({
          message: t('shareReference.search.disabledSearchTip', {
            tag: getExtraTagName(activeCategory) ?? '',
          }),
        });
      }
      return getShareReferenceData();
    }
    getFeedData();
  };

  const handleInteractionStatusChange = ({ id, voted, collected, obj_type }: UpdateInteractionStatusParams) => {
    if (exploreTab === CommunityEnum.PostReferences) {
      updateShareReferenceInteractionStatus({ id, voted, collected });
      return;
    }
    updateInteractionStatus({ id, obj_type: obj_type as CommunityEnum, voted, collected });
  };

  const { vote, unVote, collect, unCollect } = useCommunityInteraction({
    onInteractionStatusChange: (params) => handleInteractionStatusChange(params as UpdateInteractionStatusParams),
  });

  useEffect(() => {
    syncFeedDetailConfig({
      onClose: () => {
        handleLeave();
        setSelectedAsset(undefined);
      },
      onVideoPlayEnd: incrementCompleteTimes,
      source: 'inspiration' as const,
      handleVote: vote,
      handleUnVote: unVote,
      handleCollect: collect,
      handleUnCollect: unCollect,
      onChangeEpisode: handleLeave,
      onRecreateStart: () => {
        handleLeave();
        setSelectedAsset(undefined);
        router.back();
      },
      onRecreateToHome: (params) => homeGenerationFormActions?.recreateInHomeForm(params),
      thumbScrollListConfig: {
        list: inspAssets,
        hasMore,
        onLoadMore: getData,
      },
    });
  }, [inspAssets, hasMore, vote, unVote, collect, unCollect]);

  useEffect(() => {
    // 活动位和排序互斥,这里若不是混合模式，则默认推荐排序
    if (!mixed) {
      setActiveType(InspirationActiveType.Recommended);
    }
  }, [mixed]);

  const options: {
    label: string;
    value: SortByType;
  }[] = [
    {
      label: t('shareReference.rank.recommend'),
      value: 'recommend',
    },
    {
      label: t('shareReference.rank.new'),
      value: 'time',
    },
  ];
  const stickyFilterClassName = cn('sticky z-10 bg-[#0A0D11] py-3 pt-1', stickyFilterTopClassName || 'top-9');

  return (
    <div className={className}>
      {!mixed && !hideInspirationTypeFilter && exploreTab === CommunityEnum.MediaAsset && (
        <div className={cn(stickyFilterClassName, 'flex items-center justify-start gap-6 text-base leading-[20px]')}>
          {inspirationTypeTabs.map((tab) => (
            <div
              key={tab.key}
              className={cn(
                'flex flex-shrink-0 cursor-pointer flex-col font-normal text-[14px] text-system-white64 sm:text-sm',
                activeType === tab.key && 'font-medium text-white',
              )}
              onClick={() => {
                setActiveType(tab.key);
                reportEvent('explore_rank_click', {
                  rank_type: tab.key,
                  tab_name: 'inspiration',
                });
              }}
            >
              {tab.label}
            </div>
          ))}
        </div>
      )}
      {exploreTab === CommunityEnum.PostReferences && (
        <PostReferenceFilter
          className={stickyFilterClassName}
          categories={categories}
          activeCategory={activeCategory}
          setActiveCategory={(category) => {
            if (category && [likeTag.id, collectTag.id].includes(category)) {
              searchInputRef.current?.setValue('');
            }

            setActiveCategory(category);
          }}
        >
          <SettingRadioGroup
            options={options}
            className="mt-1 mr-2 w-fit rounded-8 bg-system-white10"
            activeOptionClassName="bg-[#364555] border-none"
            value={sortBy}
            onValueChange={(val) => {
              reportEvent('reference_sort_click', {
                name: (val as SortByType) === 'recommend' ? 'recommend' : 'new',
              });
              setSortBy(val as SortByType);
            }}
            optionsWrapperClassName="h-8"
            renderContent={(option) => {
              return (
                <img
                  src={SORT_ICON_MAP[option.value as SortByType]}
                  alt={option.label?.toString() || ''}
                  className="h-2 w-5"
                />
              );
            }}
          />
          <SearchInput
            ref={searchInputRef}
            className="mt-1 w-[196px] sm:hidden"
            defaultValue={searchValue}
            onSearch={setSearchValue}
            placeholder={t('shareReference.search.placeholder.explore')}
            inputClassName="focus-visible:bg-system-white10 pe-8 border-transparent !rounded-12 bg-system-white08"
            maxLength={60}
          />
        </PostReferenceFilter>
      )}
      <FeedList
        active={exploreTab}
        hasMore={hasMore}
        bannerList={bannerList || []}
        observedIndicesUpdateDeeps={[sortBy, activeCategory, activeType, exploreTab]}
        getBannerListLoading={getBannerListLoading}
        isLoading={exploreTab === CommunityEnum.PostReferences ? shareReferenceLoading : feedLoading}
        handleDetail={(id) => {
          if (exploreTab === CommunityEnum.PostReferences) {
            handleShareReferenceDetail(id);
          }
        }}
        onLoadMore={async () => getData()}
        onExpose={exploreTab === CommunityEnum.PostReferences ? onExpose : undefined}
        inspAssets={inspAssets}
        referenceList={referenceList}
        handleUnVote={unVote}
        handleVote={vote}
        onUsePostReference={
          exploreTab === CommunityEnum.PostReferences ? homeGenerationFormActions?.useSubjectInHomeForm : undefined
        }
        onRecreateToHome={
          pathname.startsWith(HOME_PAGE) ? (params) => homeGenerationFormActions?.recreateInHomeForm(params) : undefined
        }
        emptyEle={searchValue ? <SearchEmpty /> : undefined}
        fallbackContainerClassName="w-full min-h-[80vh]"
        deferEmptyUntilLoaded={true}
      />
    </div>
  );
};

export const FeedContainer = memo(FeedContainerBase);
FeedContainer.displayName = 'FeedContainer';
